import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlertMeasureEvent } from '../models/alert';
import { Configuration } from '../models/configuration';
import { CurrentUser } from '../models/current-user';
import { DashboardAdmin, DashboardClinic, UserStatus } from '../models/dashboard';
import { Department, UserSelectableItem } from '../models/department';
import { Graph } from '../models/graph';
import { Organization, UserOrganization } from '../models/organization';
import { Service } from '../models/service';
import { ServiceConfiguration } from '../models/service-configuration';
import { Alert, BreathExercise, BreathSession, PatientReport, SummaryPatient } from '../models/summary';
import { User, UserConsent } from '../models/user';
import { Work, WorkAvailable } from '../models/work';
import { Constants } from '../utilities/constants';
import { Parameters } from '../utilities/parameters';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private http: HttpClient
    //, private authService: AuthService
  ) { }

  private get<T>(uri: string): Observable<T> {
    return this.http.get<T>(Constants.apiRoot + '/' + uri);
  }

  private post<T>(uri: string, data: any): Observable<T> {
    return this.http.post<T>(Constants.apiRoot + '/' + uri, data);
  }

  // Utente Corrente
  public getCurrentUser(): Observable<CurrentUser> {
    return this.get<CurrentUser>('user/current');
  }

  public getConfiguration(): Observable<Configuration> {
    return this.get<Configuration>('user/current/settings');
  }

  public changePassword(oldValue: string, newValue: string): Observable<boolean> {
    let payload = [{
      old: oldValue,
      new: newValue
    }];

    return this.post<boolean>('user/current/change', payload);
  }

  public updateConfiguration(): Observable<boolean> {
    return this.post<boolean>('user/current/settings', Parameters.configuration);
  }

  public updateCurrentUser(user: CurrentUser): Observable<any> {
    return this.post<any>('user/current/update', user);
  }

  // Organizzazioni
  public getUsableOrganizations(): Observable<UserOrganization[]> {
    return this.get<UserOrganization[]>('configuration/organizations/usable');
  }

  public getOrganizations(): Observable<Organization[]> {
    return this.get<Organization[]>('configuration/organizations/list');
  }

  public getOrganizationsSelectable(): Observable<UserOrganization[]> {
    return this.get<UserOrganization[]>('configuration/organizations/selection');
  }

  public addOrganization(organization: Organization): Observable<Organization> {
    return this.post<Organization>('configuration/organizations/add', organization);
  }

  public updateOrganization(organization: Organization): Observable<Organization> {
    return this.post<Organization>('configuration/organizations/update', organization);
  }

  public deleteOrganization(organization: Organization): Observable<Organization> {
    return this.post<Organization>('configuration/organizations/delete', organization);
  }

  public getOrganizationDepartments(id: number): Observable<UserSelectableItem[]> {
    return this.get<UserSelectableItem[]>('configuration/organizations/departments/' + id)
  }

  // Dipartimenti
  public getDepartments(): Observable<Department[]> {
    return this.get<Department[]>('configuration/departments/list');
  }

  public addDepartment(department: Department): Observable<Department> {
    return this.post<Department>('configuration/departments/add', department);
  }

  public updateDepartment(department: Department): Observable<Department> {
    return this.post<Department>('configuration/departments/update', department);
  }

  public deleteDepartment(department: Department): Observable<Department> {
    return this.post<Department>('configuration/departments/delete', department);
  }

  public getDepartmentServices(organizationId: number, departmentId: number): Observable<ServiceConfiguration[]> {
    let data = [{
      orgId: organizationId,
      deptId: departmentId
    }];

    return this.post<ServiceConfiguration[]>('configuration/departments/features', data);
  }

  // Dashboard
  public getDashboardAdmin(id: number): Observable<DashboardAdmin> {
    return this.get<DashboardAdmin>('dashboard/admin/' + id);
  }

  public getDashboardClinic(id: string): Observable<DashboardClinic> {
    return this.get<DashboardClinic>('dashboard/clinic/' + id);
  }

  // Segnali
  public getAlertMeasures(userId: string, start: Date, end: Date): Observable<AlertMeasureEvent[]>{
    let data = [{
      userId: userId,
      start: start.getTime(),
      end: end.getTime()
    }];

    return this.post<AlertMeasureEvent[]>("measure/alert", data);
  }

  public getMeasure(userId: string, start: Date, end: Date, type: number): Observable<Graph[][]> {
    let data = [{
      userId: userId,
      start: start.toISOString(),
      end: end.toISOString(),
      measure: type
    }];

    return this.post<Graph[][]>("measure/measure", data);
  }

  public getWaveform(userId: string, start: Date, end: Date, type: number): Observable<Graph[][]> {
    let data = [{
      userId: userId,
      start: start.toISOString(),
      end: end.toISOString(),
      measure: type
    }];

    return this.post<Graph[][]>("measure/waveform", data);
  }

  // Servizi
  public getService(id: number): Observable<Service> {
    return this.get<Service>('configuration/service/' + id);
  }

  public getServices(): Observable<Service[]> {
    return this.get<Service[]>('configuration/services/list')
  }

  public updateService(service: Service): Observable<any> {
    return this.post<any>('configuration/services/update', service);
  }

  // Works
  public getWorkAvailable(): Observable<WorkAvailable[]> {
    return this.get<WorkAvailable[]>('configuration/works/available')
  }

  public getWorks(): Observable<Work[]> {
    return this.get<Work[]>('configuration/works/list')
  }

  public addWork(work: Work): Observable<any> {
    return this.post<any>('configuration/works/add', work);
  }

  public updateWork(work: Work): Observable<any> {
    return this.post<any>('configuration/works/update', work);
  }

  // Utenti
  public getUser(userId: string): Observable<User> {
    return this.get<User>('configuration/user/' + userId)
  }

  public getUsers(): Observable<User[]> {
    return this.get<User[]>('configuration/users/list')
  }

  public addUser(user: User): Observable<string> {
    return this.post<string>('configuration/users/add', user);
  }

  public updateUser(user: User): Observable<any> {
    return this.post<any>('configuration/users/update', user);
  }

  public deleteUser(user: User): Observable<any> {
    return this.post<any>('configuration/users/delete', user);
  }

  public linkUser(user: User): Observable<any> {
    return this.post<any>('configuration/users/link', user);
  }

  public resendEmail(user: User): Observable<any> {
    let data = [{
      id: user.userId
    }];
    return this.post<any>('configuration/users/resend', data);
  }

  public resetPassword(user: User): Observable<any> {
    let data = [{
      id: user.userId
    }];
    return this.post<any>('configuration/users/reset', data);
  }

  public getUserConsents(userId: string): Observable<UserConsent> {
    return this.get<UserConsent>('configuration/user/consent/' + userId)
  }

  public getUserRelations(userId: string): Observable<UserOrganization[]> {
    let data = [{
      id: userId
    }];
    return this.post<UserOrganization[]>('configuration/users/relations', data)
  }

  // Pazienti
  public getAlerts(id: string): Observable<Alert[]>{
    return this.get<Alert[]>('patient/alerts/list');
  }

  public getPatients(id: string): Observable<UserStatus[]> {
    return this.get<UserStatus[]>('patient/list/' + id);
  }

  public getPatientAlerts(id: string): Observable<Alert[]>{
    return this.get<Alert[]>('patient/alerts/' + id);
  }

  public getPatientDocument(id: string, timestamp: number, timestampEnd: number, type: number): Observable<Blob> {
    let payload=[{
      id: id,
      timestamp: timestamp,
      timestampend: timestampEnd,
      type: type
    }]

    return this.http.post(Constants.apiRoot + '/patient/document', payload, { responseType: 'blob' });
  }

  public getPatientGraphs(id: string, type: number, start: string, end: string): Observable<Graph[][]> {
    let payload=[{
      id: id,
      start: start,
      end: end,
      type: type
    }]

    return this.post<Graph[][]>('patient/graphs', payload);
  }

  public getPatientBreathExercises(userId: string, sessionId: string, timeStamp: Date): Observable<BreathExercise[]> {
    let payload=[{
      userId: userId,
      sessionId: sessionId,
      timestamp: timeStamp
    }]

    return this.post<BreathExercise[]>('patient/breath/exercises', payload);
  }

  public getPatientBreathSessions(id: string): Observable<BreathSession[]> {
    return this.get<BreathSession[]>('patient/breath/sessions/' + id);
  }

  public getPatientReport(id: string, start: Date, end: Date, currentLang: string, timeZone : string): Observable<Blob> {
    let payload=[{
      id: id,
      start: start.toISOString(),
      end: end.toISOString(),
      lang: currentLang,
      zone: timeZone
    }]

    return this.http.post(Constants.apiRoot + '/patient/report', payload, { responseType: 'blob' });
  }

  public getPatientReports(id: string): Observable<PatientReport[]>{
    return this.get<PatientReport[]>('patient/reports/' + id);
  }

  public getPatientSummary(id: string): Observable<SummaryPatient> {
    return this.get<SummaryPatient>('patient/summary/' + id);
  }

  public setPatientAlertVerified(alert: Alert): Observable<boolean> {
    return this.post<boolean>('patient/alerts/checked', alert);
  }
}
